import { DocumentBadgePlusIcon, Typography } from "@cpj-ai/panel-npm-package";
import { AuthAccountOrganizationJobPositionDegreesChoices, GetJobPositionByIdQuery } from "@graphql/generated";

import React, { useMemo } from "react";

interface Section {
  title: string;
  content?: string[] | string | null;
  type?: "list" | "text";
}

const Section: React.FC<Section> = ({ title, content, type }) => {
  if (!content || (Array.isArray(content) && content.length === 0)) {
    return null;
  }

  return (
    <div>
      <div className="flex gap-2 mb-2">
        <DocumentBadgePlusIcon className="w-6 h-6" stroke="#000" />
        <Typography>{title}</Typography>
      </div>
      {Array.isArray(content) && type === "list" ? (
        <ol className="list-decimal ml-4">
          {content.map((item) => (
            <li key={item} className="text-xs text-zinc-600 leading-6">
              {item || "-"}
            </li>
          ))}
        </ol>
      ) : (
        <Typography className="text-xs text-zinc-600 leading-6">
          {Array.isArray(content) ? content.join(", ") || "-" : content || "-"}
        </Typography>
      )}
    </div>
  );
};

interface TechnicalInformationProps {
  jobPositionData?: GetJobPositionByIdQuery;
}

export const TechnicalInformation: React.FC<TechnicalInformationProps> = ({ jobPositionData }) => {
  const jobPosition = jobPositionData?.organization?.jobPosition?.get;

  const technicalSections: Section[] = useMemo(
    () => [
      {
        title: "Skills",
        content: jobPosition?.skills
          ?.map((item) => item?.title)
          .filter((title): title is string => title !== null && title !== undefined),
      },
      {
        title: "Education",
        content: jobPosition?.degrees
          ?.filter(
            (degree): degree is AuthAccountOrganizationJobPositionDegreesChoices =>
              degree !== null && degree !== undefined,
          )
          ?.map((degree) => String(degree).toLowerCase()),
      },
      {
        title: "Performance Expectations",
        content: jobPosition?.performanceExpectation
          ? (Array.isArray(jobPosition.performanceExpectation)
              ? jobPosition.performanceExpectation
              : [jobPosition.performanceExpectation]
            ).filter(Boolean)
          : null,
        type: "list",
      },
      {
        title: "Restrictions",
        content: jobPosition?.jobRestrictions ?? null,
      },
    ],
    [jobPosition],
  );

  const nonEmptySections = technicalSections.filter(
    (section) => section.content && (!Array.isArray(section.content) || section.content.length > 0),
  );

  return (
    <div>
      {nonEmptySections.length ? (
        <>
          <Typography className="font-medium mb-6">Technical information :</Typography>
          <div className="flex flex-col gap-6">
            {nonEmptySections?.map((section) => (
              <Section
                key={`section-${section.title}`}
                title={`${section.title}:`}
                content={section.content}
                type={section.type}
              />
            ))}
          </div>
        </>
      ) : (
        <></>
      )}
    </div>
  );
};

export default TechnicalInformation;
